import React, { useEffect, useState } from 'react';
import { Plus, MapPin, Trash2, X, Loader2, RefreshCw, Building2 } from 'lucide-react';
import { teamsApi, TeamRecord, TeamRegionRow } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Regions() {
  const {
    currentWorkspace
  } = useWorkspace();
  const [team, setTeam] = useState<TeamRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newRegion, setNewRegion] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fetchTeam = async () => {
    setLoading(true);
    try {
      const teamData = await teamsApi.findByTeamName(currentWorkspace.name);
      setTeam(teamData);
    } catch (err) {
      console.error('Failed to fetch team:', err);
      toast.error('Failed to load team configuration');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTeam();
  }, [currentWorkspace.name]);
  const handleCreateTeam = async () => {
    setSaving(true);
    try {
      const teamId = teamsApi.generateTeamId(currentWorkspace.name);
      await teamsApi.create({
        Team_Name: currentWorkspace.name,
        Team_ID: teamId,
        Status: 'Active',
        Team_Regions: []
      });
      toast.success('Team configuration created');
      fetchTeam();
    } catch (err) {
      console.error('Failed to create team:', err);
      toast.error('Failed to create team configuration');
    } finally {
      setSaving(false);
    }
  };
  const handleAddRegion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRegion.trim() || !team) return;
    // Check if region already exists
    const existingRegions = team.Team_Regions || [];
    if (existingRegions.some(r => r.Region_Name === newRegion.trim())) {
      toast.error('Region already exists');
      return;
    }
    setSaving(true);
    try {
      const regionId = newRegion.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const updatedRegions: TeamRegionRow[] = [...existingRegions, {
        Region_Name: newRegion.trim(),
        Region_ID: regionId
      }];
      await teamsApi.update(team.ID, {
        Team_Regions: updatedRegions
      });
      toast.success('Region added successfully');
      setNewRegion('');
      setShowModal(false);
      fetchTeam();
    } catch (err) {
      console.error('Failed to add region:', err);
      toast.error('Failed to add region');
    } finally {
      setSaving(false);
    }
  };
  const handleDeleteRegion = async (regionName: string) => {
    if (!team) return;
    setSaving(true);
    try {
      const updatedRegions = (team.Team_Regions || []).filter(r => r.Region_Name !== regionName);
      await teamsApi.update(team.ID, {
        Team_Regions: updatedRegions
      });
      toast.success('Region removed');
      setDeleteConfirm(null);
      fetchTeam();
    } catch (err) {
      console.error('Failed to delete region:', err);
      toast.error('Failed to remove region');
    } finally {
      setSaving(false);
    }
  };
  const regions = team?.Team_Regions || [];
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-3822">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3823" />
      </div>;
  }
  // If no team configuration exists, show setup prompt
  if (!team) {
    return <div className="space-y-6" data-id="element-3824">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3825">
          <div className="min-w-0" data-id="element-3826">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3827">Regions</h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3828">
              Manage regions for {currentWorkspace.name}.
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-3829">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" data-id="element-3830" />
          <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-3831">
            Team Not Set Up Yet
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto" data-id="element-3832">
            Before adding regions, you need to initialize the team
            configuration. This will set up your team's regions and settings.
          </p>
          <button onClick={handleCreateTeam} disabled={saving} className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3833">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3834" /> : <Plus className="h-4 w-4" data-id="element-3835" />}
            Initialize Team
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3836">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3837">
        <div className="min-w-0" data-id="element-3838">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3839">Regions</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3840">
            Manage regions for {currentWorkspace.name}. Regions are used to
            scope and filter data across modules.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3841">
          <button onClick={fetchTeam} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-3842">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3843" />
          </button>
          <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-3844">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-3845" />
            <span data-id="element-3846">Add Region</span>
          </button>
        </div>
      </div>

      {/* Team Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3847">
        <div className="flex items-center gap-3" data-id="element-3848">
          <Building2 className="h-5 w-5 text-blue-600" data-id="element-3849" />
          <div data-id="element-3850">
            <div className="text-sm font-medium text-blue-900" data-id="element-3851">
              {team.Team_Name}
            </div>
            <div className="text-xs text-blue-700 font-mono" data-id="element-3852">
              ID: {team.Team_ID}
            </div>
          </div>
          <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${team.Status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-3853">
            {team.Status}
          </span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3854">
        {regions.length === 0 ? <div className="p-12 text-center" data-id="element-3855">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" data-id="element-3856" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3857">
              No regions defined
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3858">
              Add regions to segment data by geographic or logical areas.
            </p>
            <button onClick={() => setShowModal(true)} className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700" data-id="element-3859">
              <Plus className="h-4 w-4" data-id="element-3860" />
              Add your first region
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-3861">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3862">
              <thead className="bg-gray-50" data-id="element-3863">
                <tr data-id="element-3864">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3865">
                    Region Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3866">
                    Region ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3867">
                    Team
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3868">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3869">
                {regions.map(region => <tr key={region.Region_ID} className="hover:bg-gray-50" data-id="element-3870">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3871">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-3872">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" data-id="element-3873" />
                        {region.Region_Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3874">
                      <code className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded" data-id="element-3875">
                        {region.Region_ID}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3876">
                      {team.Team_Name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3877">
                      <button onClick={() => setDeleteConfirm(region.Region_Name)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-3878">
                        <Trash2 className="h-4 w-4" data-id="element-3879" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3880">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3881">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-3882" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-3883">
              <div className="flex items-center justify-between mb-4" data-id="element-3884">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3885">
                  Add New Region
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-3886">
                  <X className="h-5 w-5" data-id="element-3887" />
                </button>
              </div>
              <form onSubmit={handleAddRegion} className="space-y-4" data-id="element-3888">
                <div data-id="element-3889">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3890">
                    Region Name
                  </label>
                  <div className="relative" data-id="element-3891">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-3892" />
                    <input type="text" required className="block w-full rounded-md border-gray-300 pl-10 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRegion} onChange={e => setNewRegion(e.target.value)} placeholder="e.g., Sao Paulo, North, Retail Team" data-id="element-3893" />
                  </div>
                </div>
                <div data-id="element-3894">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3895">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team.Team_Name} disabled data-id="element-3896" />
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3897">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3898">
                    Cancel
                  </button>
                  <button type="submit" disabled={!newRegion.trim() || saving} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3899">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3900" />}
                    Add Region
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3901">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3902">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-3903" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-3904">
              <div className="flex items-center gap-4 mb-4" data-id="element-3905">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3906">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-3907" />
                </div>
                <div data-id="element-3908">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3909">
                    Remove Region
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-3910">
                    Remove "{deleteConfirm}" from {team.Team_Name}? This won't
                    delete CRM records tagged with this region.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-3911">
                <button onClick={() => setDeleteConfirm(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3912">
                  Cancel
                </button>
                <button onClick={() => handleDeleteRegion(deleteConfirm)} disabled={saving} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3913">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3914" />}
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}